import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, GripVertical, MoreHorizontal, PanelRightOpen, PanelRightClose, Clock, DollarSign, User as UserIcon, Play, CheckCircle2, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import RescheduleAppointmentDialog from "@/components/RescheduleAppointmentDialog";
import { formatBusinessTime, businessDayKey, dateObjToBusinessDayKey, isoToBusinessInput, businessInputToIso, BUSINESS_TZ } from "@/lib/businessTz";

const STATUS_COLOR = {
  scheduled: "bg-blue-500",
  in_progress: "bg-amber-500",
  completed: "bg-emerald-500",
  canceled: "bg-rose-400",
};

// Preserves the original appointment's Chicago wall-clock time-of-day and
// applies it to the target calendar date. A 10:00 AM CT job stays at
// 10:00 AM CT on the new day, even across DST transitions.
function combineDateWithOriginalTime(originalIso, targetDate) {
  const origInput = isoToBusinessInput(originalIso); // "YYYY-MM-DDTHH:MM" (CT)
  const [, timePart] = origInput.split("T");
  const [hh, mm] = (timePart || "00:00").split(":");
  const y = targetDate.getFullYear();
  const m = String(targetDate.getMonth() + 1).padStart(2, "0");
  const d = String(targetDate.getDate()).padStart(2, "0");
  // Compose the target date + original CT time, back to UTC ISO.
  return businessInputToIso(`${y}-${m}-${d}T${hh}:${mm}`);
}

export default function JobsCalendar({ jobs, onJobsChanged, highlightDays }) {
  const [view, setView] = useState("month");
  const [anchor, setAnchor] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date()); // day whose totals are shown at the top
  const [dragJob, setDragJob] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);      // Date object
  const [reschedTarget, setReschedTarget] = useState(null); // {job, proposedIso}
  const [editorJob, setEditorJob] = useState(null);         // tap-to-reschedule (mobile)
  const [drilldownOpen, setDrilldownOpen] = useState(true); // right-side day drilldown panel
  const [busyJobId, setBusyJobId] = useState(null);          // for status quick-actions
  const nav = useNavigate();

  // Keep the day-view synced with whatever the user last clicked.
  const goToDay = (d) => {
    const day = new Date(d);
    day.setHours(0,0,0,0);
    setSelectedDay(day);
    if (view === "day") setAnchor(day);
  };

  const selectedJobs = useMemo(
    () => jobsOn(jobs, selectedDay).sort((a,b)=>new Date(a.scheduled_start)-new Date(b.scheduled_start)),
    [jobs, selectedDay]
  );
  const selectedTotals = useMemo(() => {
    const total = selectedJobs.reduce((s, j) => s + (Number(j.value) || 0), 0);
    const scheduledJobs = selectedJobs.filter(j => j.status === "scheduled");
    const completedJobs = selectedJobs.filter(j => j.status === "completed");
    const scheduled = scheduledJobs.length;
    const inProgress = selectedJobs.filter(j => j.status === "in_progress").length;
    const completed = completedJobs.length;
    const scheduledGross = scheduledJobs.reduce((s,j)=>s + (Number(j.value)||0), 0);
    const completedGross = completedJobs.reduce((s,j)=>s + (Number(j.value)||0), 0);
    return { total, scheduled, inProgress, completed, scheduledGross, completedGross, count: selectedJobs.length };
  }, [selectedJobs]);
  const isSelToday = sameDay(selectedDay, new Date());

  // Week totals — full 7-day range starting on anchor's week (used for
  // the "week strip" that shows Rock River's weekly pipeline at a glance).
  const weekTotals = useMemo(() => {
    const start = startOfWeek(anchor);
    const days = Array.from({length:7}).map((_,i)=>{
      const d = new Date(start); d.setDate(d.getDate()+i);
      return { date: d, jobs: jobsOn(jobs, d) };
    });
    const flat = days.flatMap(d => d.jobs);
    const scheduledJobs = flat.filter(j=>j.status==="scheduled");
    const completedJobs = flat.filter(j=>j.status==="completed");
    return {
      start,
      end: (() => { const e = new Date(start); e.setDate(e.getDate()+6); return e; })(),
      days,
      count: flat.length,
      total: flat.reduce((s,j)=>s + (Number(j.value)||0), 0),
      scheduledGross: scheduledJobs.reduce((s,j)=>s + (Number(j.value)||0), 0),
      completedGross: completedJobs.reduce((s,j)=>s + (Number(j.value)||0), 0),
      scheduled: scheduledJobs.length,
      inProgress: flat.filter(j=>j.status==="in_progress").length,
      completed: completedJobs.length,
      busiestDay: (() => {
        const best = days.reduce((a,b)=> b.jobs.length > (a?.jobs.length||0) ? b : a, null);
        return best && best.jobs.length > 0 ? best.date : null;
      })(),
    };
  }, [jobs, anchor]);

  const updateStatus = async (job, status) => {
    if (!job?.id || busyJobId) return;
    setBusyJobId(job.id);
    try {
      await api.patch(`/jobs/${job.id}`, { status });
      toast.success(`Marked ${status.replace(/_/g," ")}`);
      onJobsChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status update failed");
    } finally {
      setBusyJobId(null);
    }
  };

  const shift = (delta) => {
    const d = new Date(anchor);
    if (view === "month") d.setMonth(d.getMonth() + delta);
    else if (view === "week") d.setDate(d.getDate() + delta * 7);
    else d.setDate(d.getDate() + delta);
    setAnchor(d);
  };

  const label = useMemo(() => {
    if (view === "month") return anchor.toLocaleString(undefined, { month: "long", year: "numeric" });
    if (view === "week") {
      const start = startOfWeek(anchor);
      const end = new Date(start); end.setDate(end.getDate() + 6);
      return `${start.toLocaleDateString(undefined,{month:"short",day:"numeric"})} – ${end.toLocaleDateString(undefined,{month:"short",day:"numeric",year:"numeric"})}`;
    }
    return anchor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  }, [anchor, view]);

  const startDrag = (e, job) => {
    setDragJob(job);
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires setData for drag to fire
    try { e.dataTransfer.setData("text/plain", job.id); } catch {}
  };

  const handleDrop = (e, targetDate) => {
    e.preventDefault();
    setDropTarget(null);
    if (!dragJob || !dragJob.scheduled_start) return;
    // Ignore no-op drops (same day)
    const origDate = new Date(dragJob.scheduled_start);
    const same = origDate.toDateString() === targetDate.toDateString();
    if (same) { setDragJob(null); return; }
    const proposedIso = combineDateWithOriginalTime(dragJob.scheduled_start, targetDate);
    setReschedTarget({ job: dragJob, proposedIso });
    setDragJob(null);
  };

  const onOpenJob = (j) => nav(`/app/jobs/${j.id}`);

  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden" data-testid="jobs-calendar">
      {/* Selected-day totals bar — defaults to today, updates when any cell is clicked */}
      <div
        className="px-4 py-3 border-b border-zinc-100 bg-gradient-to-r from-orange-50 via-white to-white flex flex-wrap items-center justify-between gap-3"
        data-testid="jobs-calendar-day-summary"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#FF5A00]/10 flex items-center justify-center">
            <span className="font-heading font-bold text-[#FF5A00]" data-testid="jobs-calendar-selected-daynum">{selectedDay.getDate()}</span>
          </div>
          <div>
            <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">
              {isSelToday ? "Today" : "Selected day"}
            </div>
            <div className="font-heading font-bold" data-testid="jobs-calendar-selected-date">
              {selectedDay.toLocaleDateString(undefined,{weekday:"long",month:"short",day:"numeric",year:"numeric"})}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <SummaryStat label="Jobs" value={selectedTotals.count} testid="jobs-calendar-day-count"/>
          <SummaryStat label="Total" value={`$${selectedTotals.total.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} testid="jobs-calendar-day-total" highlight/>
          <SummaryStat label="Sched. gross" value={`$${selectedTotals.scheduledGross.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} testid="jobs-calendar-day-scheduled-gross" dot="bg-blue-500"/>
          <SummaryStat label="Completed gross" value={`$${selectedTotals.completedGross.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} testid="jobs-calendar-day-completed-gross" dot="bg-emerald-500"/>
          <SummaryStat label="Scheduled" value={selectedTotals.scheduled} testid="jobs-calendar-day-scheduled" dot="bg-blue-500"/>
          <SummaryStat label="In prog." value={selectedTotals.inProgress} testid="jobs-calendar-day-inprogress" dot="bg-amber-500"/>
          <SummaryStat label="Done" value={selectedTotals.completed} testid="jobs-calendar-day-completed" dot="bg-emerald-500"/>
          {!isSelToday && (
            <button
              onClick={()=>goToDay(new Date())}
              className="text-xs px-2 py-1 rounded border border-zinc-200 hover:bg-zinc-50 font-mono-accent uppercase tracking-wider"
              data-testid="jobs-calendar-jump-today"
            >
              Jump to today
            </button>
          )}
        </div>
      </div>

      <div className="p-4 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button onClick={()=>shift(-1)} className="p-1.5 rounded border border-zinc-200 hover:bg-zinc-50" data-testid="calendar-prev"><ChevronLeft className="w-4 h-4"/></button>
          <button onClick={()=>{ setAnchor(new Date()); goToDay(new Date()); }} className="text-xs px-3 py-1.5 rounded border border-zinc-200 hover:bg-zinc-50" data-testid="calendar-today">Today</button>
          <button onClick={()=>shift(1)} className="p-1.5 rounded border border-zinc-200 hover:bg-zinc-50" data-testid="calendar-next"><ChevronRight className="w-4 h-4"/></button>
          <span className="ml-2 font-heading font-bold">{label}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden md:inline-flex items-center text-[10px] font-mono-accent uppercase text-zinc-500">
            <GripVertical className="w-3 h-3 mr-0.5"/> drag to reschedule
          </span>
          <button
            onClick={()=>setDrilldownOpen(v=>!v)}
            className="hidden lg:inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded border border-zinc-200 hover:bg-zinc-50"
            title={drilldownOpen ? "Hide day drilldown" : "Show day drilldown"}
            data-testid="jobs-calendar-toggle-drilldown"
          >
            {drilldownOpen ? <PanelRightClose className="w-3.5 h-3.5"/> : <PanelRightOpen className="w-3.5 h-3.5"/>}
            Day panel
          </button>
          <div className="flex gap-1">
            {["day","week","month"].map((v) => (
              <button key={v} onClick={()=>{ setView(v); if (v === "day") setAnchor(selectedDay); }} className={`text-xs px-3 py-1.5 rounded border capitalize ${view===v?"bg-zinc-900 text-white border-zinc-900":"bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"}`} data-testid={`calendar-view-${v}`}>{v}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Week totals strip — visible in week view */}
      {view === "week" && (
        <WeekStrip totals={weekTotals} onJumpDay={goToDay} data-testid="jobs-calendar-week-strip"/>
      )}

      <div className={`${drilldownOpen ? "lg:grid lg:grid-cols-[minmax(0,1fr)_320px]" : ""}`}>
        <div className="min-w-0">
          {view === "month" && (
            <MonthGrid
              anchor={anchor} jobs={jobs}
              highlightDays={highlightDays}
              selectedDay={selectedDay}
              onSelectDay={goToDay}
              onOpen={onOpenJob}
              onStartDrag={startDrag}
              onDrop={handleDrop}
              dropTarget={dropTarget}
              setDropTarget={setDropTarget}
              onReschedTap={setEditorJob}
            />
          )}
          {view === "week" && (
            <WeekGrid
              anchor={anchor} jobs={jobs}
              selectedDay={selectedDay}
              onSelectDay={goToDay}
              onOpen={onOpenJob}
              onStartDrag={startDrag}
              onDrop={handleDrop}
              dropTarget={dropTarget}
              setDropTarget={setDropTarget}
              onReschedTap={setEditorJob}
            />
          )}
          {view === "day" && (
            <DayList anchor={selectedDay} jobs={jobs} onOpen={onOpenJob} onReschedTap={setEditorJob}/>
          )}
        </div>

        {drilldownOpen && (
          <DayDrilldown
            selectedDay={selectedDay}
            jobs={selectedJobs}
            onOpenJob={onOpenJob}
            onReschedule={setEditorJob}
            onStatusChange={updateStatus}
            busyJobId={busyJobId}
          />
        )}
      </div>

      {/* Quick confirmation after drop */}
      <RescheduleAppointmentDialog
        open={!!reschedTarget}
        onOpenChange={(v)=>!v && setReschedTarget(null)}
        job={reschedTarget?.job}
        proposedStartIso={reschedTarget?.proposedIso}
        onRescheduled={()=>{ setReschedTarget(null); onJobsChanged?.(); }}
      />

      {/* Editor mode (tap on mobile OR keyboard-only users) */}
      <RescheduleAppointmentDialog
        open={!!editorJob}
        onOpenChange={(v)=>!v && setEditorJob(null)}
        job={editorJob}
        onRescheduled={()=>{ setEditorJob(null); onJobsChanged?.(); }}
      />
    </div>
  );
}

function startOfWeek(d) {
  const x = new Date(d);
  x.setHours(0,0,0,0);
  x.setDate(x.getDate() - x.getDay());
  return x;
}
function sameDay(a, b) {
  // Two Date objects are "the same day" when their wall-clock Y/M/D match.
  // Both inputs here are already treated as business-timezone calendar days
  // (we only read Y/M/D parts), so this is correct across DST and remote
  // technician timezones.
  return a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
}
function jobsOn(jobs, day) {
  // Group jobs by business-timezone (America/Chicago) calendar day, not by
  // the browser's local Date — otherwise a 9 PM CT job silently moves to
  // the next day for anyone east of CT or the previous day for Mountain
  // Time, which is what caused the schedule to appear "off" from the
  // Estimate time.
  const key = dateObjToBusinessDayKey(day);
  return jobs.filter((j) => j.scheduled_start && businessDayKey(j.scheduled_start) === key);
}

function JobPill({ job, size = "sm", onOpen, onStartDrag, onReschedTap }) {
  const draggable = !!job.scheduled_start && job.status !== "canceled" && job.status !== "completed";
  return (
    <div
      draggable={draggable}
      onDragStart={draggable ? (e) => onStartDrag(e, job) : undefined}
      onClick={()=>onOpen(job)}
      className={`group relative w-full text-left rounded ${size==="lg"?"p-2 text-xs":"px-1.5 py-0.5 text-[10px]"} text-white truncate cursor-pointer hover:opacity-90 ${STATUS_COLOR[job.status]||"bg-zinc-400"} ${draggable ? "cursor-grab active:cursor-grabbing" : ""}`}
      data-testid={`calendar-job-${job.id}`}
    >
      {draggable && (
        <GripVertical className="w-3 h-3 absolute right-0.5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-70 pointer-events-none"/>
      )}
      {size === "lg" ? (
        <>
          <div className="font-medium truncate pr-3">{job.title}</div>
          <div className="text-[10px] font-mono-accent opacity-90">
            {formatBusinessTime(job.scheduled_start)} CT
          </div>
        </>
      ) : (
        <span className="truncate block pr-3">{job.title}</span>
      )}
      {/* Tap-to-reschedule affordance (mobile / no-hover) */}
      {draggable && (
        <button
          type="button"
          onClick={(e)=>{ e.stopPropagation(); onReschedTap(job); }}
          className="absolute right-0 top-0 h-full px-1 opacity-0 group-hover:opacity-100 md:opacity-0 opacity-100 bg-black/10 hover:bg-black/20"
          title="Reschedule"
          data-testid={`calendar-job-resched-${job.id}`}
        >
          <MoreHorizontal className="w-3 h-3"/>
        </button>
      )}
    </div>
  );
}

function SummaryStat({ label, value, testid, dot, highlight }) {
  return (
    <div className="flex flex-col items-end" data-testid={testid}>
      <div className={`text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500 flex items-center gap-1`}>
        {dot && <span className={`w-2 h-2 rounded-full ${dot}`}/>}
        {label}
      </div>
      <div className={`font-heading font-bold ${highlight ? "text-lg text-[#FF5A00]" : "text-sm"}`}>{value}</div>
    </div>
  );
}

// Week rollup strip — mirrors the day summary but for the full 7-day window.
function WeekStrip({ totals, onJumpDay }) {
  const label = `${totals.start.toLocaleDateString(undefined,{month:"short",day:"numeric"})} – ${totals.end.toLocaleDateString(undefined,{month:"short",day:"numeric"})}`;
  return (
    <div
      className="px-4 py-2.5 border-b border-zinc-100 bg-gradient-to-r from-blue-50/70 via-white to-white flex flex-wrap items-center justify-between gap-3"
      data-testid="jobs-calendar-week-strip"
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
          <CalendarClock className="w-4 h-4 text-blue-600"/>
        </div>
        <div>
          <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">Week</div>
          <div className="font-heading font-bold text-sm" data-testid="jobs-calendar-week-range">{label}</div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <SummaryStat label="Jobs"      value={totals.count}       testid="jobs-calendar-week-count"/>
        <SummaryStat label="Total"     value={`$${totals.total.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`}
                       testid="jobs-calendar-week-total" highlight/>
        <SummaryStat
          label="Sched. gross"
          value={`$${totals.scheduledGross.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`}
          testid="jobs-calendar-week-scheduled-gross"
          dot="bg-blue-500"
        />
        <SummaryStat
          label="Completed gross"
          value={`$${totals.completedGross.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`}
          testid="jobs-calendar-week-completed-gross"
          dot="bg-emerald-500"
        />
        <SummaryStat label="Scheduled" value={totals.scheduled}   testid="jobs-calendar-week-scheduled" dot="bg-blue-500"/>
        <SummaryStat label="In prog."  value={totals.inProgress}  testid="jobs-calendar-week-inprogress" dot="bg-amber-500"/>
        <SummaryStat label="Done"      value={totals.completed}   testid="jobs-calendar-week-completed" dot="bg-emerald-500"/>
        {totals.busiestDay && (
          <button
            onClick={()=>onJumpDay(totals.busiestDay)}
            className="text-xs px-2 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50 font-mono-accent uppercase tracking-wider"
            data-testid="jobs-calendar-week-busiest"
          >
            Busiest: {totals.busiestDay.toLocaleDateString(undefined,{weekday:"short",day:"numeric"})}
          </button>
        )}
      </div>
    </div>
  );
}

// Right-hand day drilldown — lists selected-day jobs with quick actions.
const STATUS_LABEL = {
  scheduled: "Scheduled", in_progress: "In progress",
  completed: "Completed", canceled: "Canceled",
};

function DayDrilldown({ selectedDay, jobs, onOpenJob, onReschedule, onStatusChange, busyJobId }) {
  const isToday = sameDay(selectedDay, new Date());
  return (
    <aside
      className="border-t lg:border-t-0 lg:border-l border-zinc-100 bg-zinc-50/60 max-h-[720px] lg:overflow-y-auto"
      data-testid="jobs-calendar-drilldown"
    >
      <div className="sticky top-0 z-10 bg-white border-b border-zinc-100 px-4 py-3">
        <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">
          {isToday ? "Today · Drilldown" : "Selected · Drilldown"}
        </div>
        <div className="font-heading font-bold" data-testid="jobs-calendar-drilldown-date">
          {selectedDay.toLocaleDateString(undefined,{weekday:"long",month:"short",day:"numeric"})}
        </div>
        <div className="text-xs text-zinc-500 mt-1">{jobs.length} job{jobs.length===1?"":"s"} · quick reschedule + status</div>
      </div>

      <div className="p-3 space-y-2" data-testid="jobs-calendar-drilldown-list">
        {jobs.length === 0 && (
          <div className="text-center text-xs text-zinc-500 py-8">
            No jobs on this day.<br/>
            <span className="text-zinc-400">Click any calendar cell to explore another day.</span>
          </div>
        )}
        {jobs.map((j) => (
          <div
            key={j.id}
            className="bg-white border border-zinc-200 rounded-lg p-3 hover:border-[#FF5A00] transition-colors"
            data-testid={`drilldown-job-${j.id}`}
          >
            <button
              onClick={()=>onOpenJob(j)}
              className="w-full text-left"
              data-testid={`drilldown-open-${j.id}`}
            >
              <div className="flex items-start gap-2">
                <div className={`w-1.5 h-full min-h-[36px] rounded-full ${STATUS_COLOR[j.status]||"bg-zinc-400"}`}/>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{j.title}</div>
                  <div className="text-[11px] text-zinc-500 truncate flex items-center gap-1 mt-0.5">
                    <UserIcon className="w-3 h-3"/> {j.customer_name || "—"}
                  </div>
                  <div className="text-[11px] text-zinc-500 flex items-center gap-2 mt-0.5 font-mono-accent">
                    <span className="inline-flex items-center gap-1" title={BUSINESS_TZ}><Clock className="w-3 h-3"/>{formatBusinessTime(j.scheduled_start)} CT</span>
                    <span className="inline-flex items-center gap-1"><DollarSign className="w-3 h-3"/>{(Number(j.value)||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                  </div>
                </div>
                <span className={`text-[9px] font-mono-accent uppercase tracking-wider px-1.5 py-0.5 rounded text-white ${STATUS_COLOR[j.status]||"bg-zinc-400"}`}>
                  {STATUS_LABEL[j.status]||j.status}
                </span>
              </div>
            </button>

            <div className="mt-2 pt-2 border-t border-zinc-100 flex flex-wrap gap-1.5">
              <button
                onClick={()=>onReschedule(j)}
                className="text-[11px] px-2 py-1 rounded border border-zinc-200 hover:bg-zinc-50 inline-flex items-center gap-1"
                data-testid={`drilldown-reschedule-${j.id}`}
                disabled={busyJobId === j.id}
              >
                <CalendarClock className="w-3 h-3"/> Reschedule
              </button>
              {j.status !== "scheduled" && j.status !== "canceled" && (
                <button
                  onClick={()=>onStatusChange(j, "scheduled")}
                  className="text-[11px] px-2 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50 inline-flex items-center gap-1"
                  data-testid={`drilldown-status-scheduled-${j.id}`}
                  disabled={busyJobId === j.id}
                >
                  Reset
                </button>
              )}
              {j.status !== "in_progress" && j.status !== "completed" && j.status !== "canceled" && (
                <button
                  onClick={()=>onStatusChange(j, "in_progress")}
                  className="text-[11px] px-2 py-1 rounded border border-amber-200 text-amber-700 hover:bg-amber-50 inline-flex items-center gap-1"
                  data-testid={`drilldown-status-inprogress-${j.id}`}
                  disabled={busyJobId === j.id}
                >
                  <Play className="w-3 h-3"/> Start
                </button>
              )}
              {j.status !== "completed" && j.status !== "canceled" && (
                <button
                  onClick={()=>onStatusChange(j, "completed")}
                  className="text-[11px] px-2 py-1 rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1"
                  data-testid={`drilldown-status-completed-${j.id}`}
                  disabled={busyJobId === j.id}
                >
                  <CheckCircle2 className="w-3 h-3"/> Complete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}

function MonthGrid({ anchor, jobs, onOpen, onStartDrag, onDrop, dropTarget, setDropTarget, onReschedTap, selectedDay, onSelectDay, highlightDays }) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = startOfWeek(first);
  const days = Array.from({ length: 42 }).map((_, i) => {
    const d = new Date(gridStart); d.setDate(d.getDate() + i); return d;
  });
  const today = new Date();
  return (
    <div>
      <div className="grid grid-cols-7 border-b border-zinc-100">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
          <div key={d} className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500 text-center py-2">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d, i) => {
          const isMonth = d.getMonth() === anchor.getMonth();
          const isToday = sameDay(d, today);
          const isSelected = selectedDay && sameDay(d, selectedDay);
          const dayJobs = jobsOn(jobs, d);
          const isDropOver = dropTarget && sameDay(dropTarget, d);
          const near = highlightDays?.[dateObjToBusinessDayKey(d)];
          return (
            <div
              key={i}
              onClick={()=>onSelectDay?.(d)}
              onDragOver={(e)=>{ e.preventDefault(); setDropTarget(d); }}
              onDragLeave={()=>setDropTarget((cur)=>cur && sameDay(cur,d) ? null : cur)}
              onDrop={(e)=>onDrop(e, d)}
              className={`min-h-[110px] border-b border-r border-zinc-100 p-2 cursor-pointer ${isMonth?"bg-white":"bg-zinc-50/50"} ${isSelected?"bg-orange-50 ring-2 ring-inset ring-[#FF5A00]":isToday?"ring-2 ring-inset ring-[#FF5A00]/40":""} ${isDropOver?"bg-emerald-50 ring-2 ring-inset ring-emerald-400":""} ${near && !isSelected ? "!bg-emerald-50" : ""}`}
              data-testid={`calendar-cell-${d.toISOString().slice(0,10)}`}
            >
              <div className={`text-xs font-mono-accent ${isMonth?"text-zinc-800":"text-zinc-400"} ${isToday||isSelected?"font-bold":""}`}>{d.getDate()}</div>
              {near && (
                <div className="mt-0.5 inline-block text-[10px] font-semibold text-emerald-800 bg-emerald-100 border border-emerald-300 rounded px-1" title={`Near ${near.title || "a booked job"} · ${near.service_address || ""}`} data-testid={`calendar-nearby-${dateObjToBusinessDayKey(d)}`}>
                  {near.miles} mi · {near.open_slots} open
                </div>
              )}
              <div className="mt-1 space-y-1">
                {dayJobs.slice(0,3).map((j) => (
                  <JobPill key={j.id} job={j} onOpen={onOpen} onStartDrag={onStartDrag} onReschedTap={onReschedTap}/>
                ))}
                {dayJobs.length > 3 && <div className="text-[10px] text-zinc-500">+{dayJobs.length-3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekGrid({ anchor, jobs, onOpen, onStartDrag, onDrop, dropTarget, setDropTarget, onReschedTap, selectedDay, onSelectDay }) {
  const start = startOfWeek(anchor);
  const days = Array.from({length:7}).map((_,i)=>{ const d=new Date(start); d.setDate(d.getDate()+i); return d; });
  return (
    <div className="grid grid-cols-7">
      {days.map((d, i) => {
        const dayJobs = jobsOn(jobs, d).sort((a,b)=>new Date(a.scheduled_start)-new Date(b.scheduled_start));
        const isDropOver = dropTarget && sameDay(dropTarget, d);
        const isSelected = selectedDay && sameDay(d, selectedDay);
        return (
          <div key={i}
            onClick={()=>onSelectDay?.(d)}
            onDragOver={(e)=>{ e.preventDefault(); setDropTarget(d); }}
            onDragLeave={()=>setDropTarget((cur)=>cur && sameDay(cur,d) ? null : cur)}
            onDrop={(e)=>onDrop(e, d)}
            className={`min-h-[400px] border-r border-zinc-100 last:border-r-0 p-2 cursor-pointer ${isSelected?"bg-orange-50 ring-2 ring-inset ring-[#FF5A00]":isDropOver?"bg-emerald-50":""}`}
            data-testid={`calendar-week-cell-${d.toISOString().slice(0,10)}`}
          >
            <div className="text-center mb-2">
              <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">{d.toLocaleDateString(undefined,{weekday:"short"})}</div>
              <div className={`font-heading text-lg font-bold ${sameDay(d,new Date())?"text-[#FF5A00]":""}`}>{d.getDate()}</div>
            </div>
            <div className="space-y-1.5">
              {dayJobs.map((j)=>(
                <JobPill key={j.id} job={j} size="lg" onOpen={onOpen} onStartDrag={onStartDrag} onReschedTap={onReschedTap}/>
              ))}
              {dayJobs.length === 0 && <div className="text-[10px] text-zinc-400 text-center py-2">—</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DayList({ anchor, jobs, onOpen, onReschedTap }) {
  const dayJobs = jobsOn(jobs, anchor).sort((a,b)=>new Date(a.scheduled_start)-new Date(b.scheduled_start));
  return (
    <div className="p-6">
      {dayJobs.length === 0 ? (
        <div className="text-center text-zinc-500 py-12">No jobs scheduled for this day.</div>
      ) : (
        <div className="space-y-2">
          {dayJobs.map((j)=>(
            <div key={j.id} className="w-full flex items-center gap-4 p-4 border border-zinc-200 rounded-lg hover:border-[#FF5A00] hover:bg-orange-50" data-testid={`calendar-day-job-${j.id}`}>
              <button onClick={()=>onOpen(j)} className="flex-1 flex items-center gap-4 text-left">
                <div className={`w-2 h-12 rounded-full ${STATUS_COLOR[j.status]||"bg-zinc-400"}`}/>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{j.title}</div>
                  <div className="text-xs text-zinc-500">{j.customer_name} · {j.service_address || "—"}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono-accent text-sm">{new Date(j.scheduled_start).toLocaleTimeString([],{hour:"numeric",minute:"2-digit"})}</div>
                  <div className="text-xs text-zinc-500">${(j.value||0).toLocaleString()}</div>
                </div>
              </button>
              <button onClick={(e)=>{e.stopPropagation(); onReschedTap(j);}} className="text-xs text-[#FF5A00] px-3 py-1.5 border border-[#FF5A00]/30 rounded hover:bg-orange-50"
                data-testid={`calendar-day-resched-${j.id}`}>
                Reschedule
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
